import { toggle } from '@/features/sidebar';
import { useCustomerFeedback } from '@/features/customer-feedback';
import { useNetworkStatus } from '@/hooks/browser/use-network-status';
import { useActionCooldown } from '@/hooks/user-actions/use-action-cooldown';
import { useAnalytics } from '@/services/analytics';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import { useRouter } from '@tanstack/react-router';
import {
  SUPPORTED_FILE_TYPES,
  type CopilotMessage,
  type CopilotSession,
  type CopilotUploadFile,
} from '@customerio/hydra-copilot';
import {
  useCopilotApprovalMode,
  useCopilotFileUpload,
  useCopilotQueuedMessages,
  useCopilotRestoredMessages,
  useCopilotStatus,
  useCopilotWebSearch,
} from '@customerio/hydra-copilot/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import {
  Banner,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  Form,
  Icon,
  InlineStack,
  KeyboardShortcut,
  Paragraph,
  Stack,
  Strong,
  Text,
  Tooltip,
  Truncated,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { useCurrentWorkspace } from '@/context/current-workspace';
import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMatches } from '@/services/router/compat';
import { useEditor, EditorContent } from '@tiptap/react';
import { Document } from '@tiptap/extension-document';
import { Paragraph as TiptapParagraph } from '@tiptap/extension-paragraph';
import { Text as TiptapText } from '@tiptap/extension-text';
import { HardBreak } from '@tiptap/extension-hard-break';
import { Placeholder, UndoRedo } from '@tiptap/extensions';
import * as chatStyles from '../../chat.css';
import { syncPlaceholder } from './sync-placeholder';
import { Attachments } from '../attachments';
import { FileUploadArea, type FileUploadAreaRef } from '../file-upload-area';
import { uploadFilesWithRetry } from '../../helpers/file-utils';
import { getEditorText, buildDocFromText } from './editor-text';
import { SlashCommandExtension, SkillHighlightExtension, createSuggestionRender } from './slash-commands';
import type { BuiltinCommand, SkillCommand, SuggestionRenderState } from './slash-commands';
import { useCopilotSkills } from '../../hooks/use-copilot-skills';
import { AgentUsagePill } from '../agent-usage-signals';
import { ModelPicker } from './model-picker';
import { useAutoModeConfirmation } from './auto-mode-confirm';
import * as styles from '../../index.css';

// Send and Stop share one button, so the second click of a double-click lands on the other
// action: Send then Stop kills the new message, Stop then Send re-sends the restored text.
const SEND_STOP_COOLDOWN_MS = 500;

function useTokenUsage(session: CopilotSession) {
  const messages = useStore(session.__messages);

  return useMemo(() => {
    // Scan backwards for the most recent meta message with token usage
    for (let index = messages.length - 1; index >= 0; index--) {
      const message = messages[index];
      if (message.role === 'model' && message.type === 'meta' && 'meta' in message) {
        const meta = message.meta;
        if (meta?.prompt_tokens_used != null && meta.prompt_tokens_used > 0) {
          return {
            promptTokensUsed: meta.prompt_tokens_used,
            contextWindowSize: meta.context_window_size ?? 0,
          };
        }
      }
    }
    return null;
  }, [messages]);
}

/** The memory pill appears at this percent of the context window. */
const MEMORY_PILL_PERCENT = 80;

function tokenUsagePercentage(usage: ReturnType<typeof useTokenUsage>): number | null {
  if (usage == null || usage.contextWindowSize <= 0) {
    return null;
  }
  return Math.round((usage.promptTokensUsed / usage.contextWindowSize) * 100);
}

type TokenUsageProps = { session: CopilotSession };

function TokenUsage({ session }: TokenUsageProps) {
  const usage = useTokenUsage(session);
  const percentage = tokenUsagePercentage(usage);

  if (percentage == null || percentage < MEMORY_PILL_PERCENT) {
    return <Box />;
  }

  const tooltipContent = `This conversation is using ${percentage}% of the available memory. As it grows, older details may be dropped to fit new messages.`;

  return (
    <Tooltip content={tooltipContent} isCustom={true}>
      {(context) => (
        <Paragraph
          text="product-pxs-secondary"
          color="secondary"
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        >
          {percentage}% memory used
        </Paragraph>
      )}
    </Tooltip>
  );
}

type FooterStatmentProps = { input: string };

function FooterStatment({ input }: FooterStatmentProps) {
  const networkStatus = useNetworkStatus();

  return (
    <Paragraph text="product-pxs-secondary" pt="050" color="secondary">
      {networkStatus === 'offline' ? (
        <>{input.length > 0 ? <>You can't send messages when offline.</> : <>You are currently offline.</>}</>
      ) : input.length > 0 ? (
        <>
          <Strong weight="medium">Shift + Return</Strong> to add a new line
        </>
      ) : (
        <>The Agent can make mistakes. Double-check its responses.</>
      )}
    </Paragraph>
  );
}

type QueuedMessageProps = { messages: (CopilotMessage & { text: string })[] };

function QueuedMessage({ messages }: QueuedMessageProps) {
  if (messages.length === 0) {
    return null;
  }

  return (
    <Stack gap="050">
      {messages.map((m, index) => (
        <Text key={index} size="xs" color="secondary">
          <Truncated>{m.text}</Truncated>
        </Text>
      ))}
    </Stack>
  );
}

function isTextMessage(m: CopilotMessage): m is CopilotMessage & { text: string } {
  return m.type === 'text' && 'text' in m;
}

type InputProps = {
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  session: CopilotSession;
  onSubmit?: () => void;
  placeholder?: string;
  wrapperClassName?: string;
  resetSession?: () => CopilotSession;
};

export function Input({
  session,
  input,
  setInput,
  onSubmit,
  placeholder: customPlaceholder,
  wrapperClassName: customWrapperClassName,
  resetSession,
}: InputProps) {
  const lastInput = useRef<string | null>(null);
  const lastFiles = useRef<CopilotUploadFile[]>([]);
  const fileUploadAreaRef = useRef<FileUploadAreaRef>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const snackbars = useSnackbars();
  const { track } = useAnalytics();
  const status = useCopilotStatus(session);
  const isLoading = resetSession == null && (status === 'sending' || status === 'receiving' || status === 'recovering');
  const allowSendOrStop = useActionCooldown(SEND_STOP_COOLDOWN_MS);
  const queuedMessages = useCopilotQueuedMessages(session);
  const restoredMessages = useCopilotRestoredMessages(session);
  // Filter to only user-visible text messages — tool_response messages (e.g. from
  // auto-approve) are system messages that shouldn't appear in the queued messages UI.
  const visibleQueuedMessages = useMemo(() => queuedMessages.filter(isTextMessage), [queuedMessages]);
  const networkStatus = useNetworkStatus();
  const memoryPercentage = tokenUsagePercentage(useTokenUsage(session));
  const showMemoryPill = memoryPercentage != null && memoryPercentage >= MEMORY_PILL_PERCENT;

  // File upload functionality
  const fileUpload = useCopilotFileUpload(session);

  // Handle files from both drag drop and file input
  const handleFiles = useCallback(
    (files: File[]) => {
      const result = fileUpload.addFiles(files);

      const messages: string[] = [];

      if (result.errors.length > 0) {
        messages.push(...result.errors.map((error) => `${error.name}: ${error.error}`));
      }

      if (messages.length > 0) {
        setUploadError(messages.join('\n'));
      }

      // Immediately start uploading the files with retry for transient network errors
      if (result.addedCount > 0) {
        const pendingFiles = session.getPendingUploadFiles();
        uploadFilesWithRetry(pendingFiles, fileUpload.uploadFile, fileUpload.removeFile)
          .then((uploadResult) => {
            // Handle failed uploads - accumulate with validation errors
            if (uploadResult.failed.length > 0) {
              const failedMessages = uploadResult.failed.map((f) => `${f.name}: ${f.error}`);

              // Append to existing validation errors if any
              setUploadError((previousError) => {
                if (!isEmptyString(previousError)) {
                  return previousError + '\n' + failedMessages.join('\n');
                }
                return failedMessages.join('\n');
              });

              // Report to Sentry
              for (const f of uploadResult.failed) {
                sentryCaptureException(new Error(`File upload failed for ${f.name}: ${f.error}`));
              }
            } else if (uploadResult.successful.length > 0 && messages.length === 0) {
              // Clear error if all uploads succeeded and there were no validation errors
              setUploadError(null);
            }

            if (uploadResult.successful.length > 0) {
              const fileTypes = uploadResult.successful.map((f) => {
                const extension = f.name.split('.').pop()?.toLowerCase() ?? 'unknown';
                return extension;
              });
              track('aiAssistant', 'fileUploaded', {
                Count: uploadResult.successful.length,
                'File Types': fileTypes,
              });
            }
          })
          .catch((error) => {
            // This catch should rarely be hit since uploadFilesWithRetry uses Promise.allSettled
            sentryCaptureException(error);
            setUploadError(error instanceof Error ? error.message : 'Failed to upload files.');
          });
      }
    },
    [fileUpload, session, track],
  );

  const fileChips = useMemo(
    () =>
      fileUpload.files.map((file) => ({
        id: file.id,
        name: file.name,
        size: file.size,
        mimeType: file.mimeType,
        source: file.file,
        status: file.status,
      })),
    [fileUpload.files],
  );

  async function submit(input: string, files: CopilotUploadFile[] = []) {
    setError(null);
    try {
      // Create message payload
      const messages: CopilotMessage[] = [];

      // Add file messages for uploaded files
      const uploadedFiles = files.filter((f) => f.status === 'uploaded' && f.uploadedUrl != null);
      for (const file of uploadedFiles) {
        messages.push({
          role: 'user',
          type: 'content',
          content: file.uploadedUrl ?? '',
          mime_type: file.mimeType,
          display_name: file.name,
        });
      }

      // Add text message if there's input
      if (!isEmptyString(input)) {
        messages.push({
          role: 'user',
          type: 'text',
          text: input,
        });
      }

      // Clear input and files indicating that the user has sent the message
      setInput('');
      fileUpload.clearFiles();

      // If we have messages to send, send them
      if (messages.length > 0) {
        const targetSession = resetSession?.() ?? session;
        // A new-conversation submit swaps the session, so the pick has to follow it.
        targetSession.setModel(session.getModel());
        onSubmit?.();
        // Read messages imperatively (not via hook subscription) to avoid
        // re-rendering the input component on every streaming message update.
        const currentMessages = targetSession.__statefulMessages.get();
        const userTurnCount = currentMessages.filter((m) => m.role === 'user').length;
        await targetSession.sendMessage(messages);
        track('aiAssistant', 'messageSent', {
          Message: input,
          'Has Files': uploadedFiles.length > 0,
          'File Count': uploadedFiles.length,
          'Turn Number': userTurnCount + 1,
          'Is First Message': userTurnCount === 0,
          'Conversation ID': targetSession.id,
        });
      }
    } catch (error) {
      sentryCaptureException(error);
      setError(error instanceof Error ? error.message : 'Your message was not sent.');
    }
  }

  const handleUploadButtonClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (fileUploadAreaRef.current != null) {
      fileUploadAreaRef.current.triggerFileInput();
    }
  }, []);

  // Functional updater so we prepend onto the latest input, since the user may have typed since.
  const restoreMessagesToInput = useCallback(
    (messages: CopilotMessage[]) => {
      const texts = messages.filter(isTextMessage).map((m) => m.text);
      if (texts.length === 0) return;
      const queued = texts.join('\n\n');
      setInput((previous) => [queued, previous].filter((t) => t.trim() !== '').join('\n\n'));
    },
    [setInput],
  );

  // Recovery gave up with a queued message: restore its text to the input like stop() does
  // (text only). The ref guards strict-mode/concurrent double-invoke before the clear lands.
  const consumedRestoredRef = useRef<CopilotMessage[] | null>(null);
  useEffect(() => {
    if (restoredMessages.length === 0) return;
    if (consumedRestoredRef.current === restoredMessages) return;
    consumedRestoredRef.current = restoredMessages;
    restoreMessagesToInput(restoredMessages);
    session.clearRestoredMessages();
  }, [restoredMessages, restoreMessagesToInput, session]);

  const handleStop = useCallback(async () => {
    if (!allowSendOrStop()) {
      return;
    }
    track('aiAssistant', 'stopClicked', {});
    const { messages, error } = await session.stop();

    // Always restore queued messages to the input box, even if the backend
    // cancellation failed — the local stream is already aborted either way.
    restoreMessagesToInput(messages);

    if (error != null) {
      sentryCaptureException(error);
      snackbars.create({
        message: 'The response could not be stopped. Try again.',
        variant: 'critical',
      });
    }
  }, [session, restoreMessagesToInput, snackbars, track, allowSendOrStop]);

  const approvalMode = useCopilotApprovalMode(session);
  const confirmAutoMode = useAutoModeConfirmation();
  // Both handlers no-op when the requested mode is already active — otherwise re-clicking
  // the checked Auto row would re-open the confirmation modal even though no Ask → Auto
  // switch is happening.
  const handleSelectAskMode = useCallback(() => {
    if (approvalMode === 'ask') return;
    session.setApprovalMode('ask');
    track('aiAssistant', 'approvalModeChanged', { Mode: 'ask' });
  }, [approvalMode, session, track]);
  const handleSelectAutoMode = useCallback(() => {
    if (approvalMode === 'auto') return;
    confirmAutoMode(() => {
      session.setApprovalMode('auto');
      track('aiAssistant', 'approvalModeChanged', { Mode: 'auto' });
    });
  }, [approvalMode, confirmAutoMode, session, track]);
  // Hovering the approval-mode badge shows a tooltip; we suppress it while the dropdown
  // is open so the two surfaces don't visually overlap.
  const [isApprovalDropdownOpen, setIsApprovalDropdownOpen] = useState(false);

  // The approval-mode selector only makes sense when the agent may edit live data.
  const allowAgentEditLiveData = useFeatureFlag(AccountFeatureFlag.AllowAgentEditLiveData);
  const showApprovalModeControl = allowAgentEditLiveData === true;
  // Clamp the wire value to "ask" when the control isn't shown, so a stored "auto" never leaks.
  // Layout effect: the clamped mode drives the trigger label, so it must land before paint.
  useLayoutEffect(() => {
    session.setApprovalModeAvailable(showApprovalModeControl);
  }, [session, showApprovalModeControl]);

  const enableWebSearch = useCopilotWebSearch(session);
  const workspace = useCurrentWorkspace();
  const safeNavigate = useSafeNavigate();
  const router = useRouter();
  const showFeedbackModal = useCustomerFeedback();
  const matches = useMatches();
  const lastMatch = matches[matches.length - 1];

  const hasContent = useMemo<boolean>(
    () => !isEmptyString(input) || fileUpload.files.length > 0,
    [input, fileUpload.files],
  );
  const hasUploadingFiles = useMemo<boolean>(
    () => fileUpload.files.some((f) => f.status === 'uploading' || f.status === 'pending'),
    [fileUpload.files],
  );
  const canSubmit = hasContent && !hasUploadingFiles;

  const formRef = useRef<HTMLFormElement>(null);
  // Stable refs for values needed inside tiptap keyboard shortcuts and paste
  // handlers. These closures are captured once when the editor is created, so
  // we use refs to always read the latest value.
  const visibleQueuedMessagesRef = useRef(visibleQueuedMessages);
  visibleQueuedMessagesRef.current = visibleQueuedMessages;
  const inputRef = useRef(input);
  inputRef.current = input;
  const handleFilesRef = useRef(handleFiles);
  handleFilesRef.current = handleFiles;
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const setInputRef = useRef(setInput);
  setInputRef.current = setInput;

  const placeholderText =
    fileChips.length > 0 ? 'Add a message (optional)' : (customPlaceholder ?? 'How can I help you today?');

  const copilotSkills = useCopilotSkills();
  const skillCommands = useMemo<SkillCommand[]>(
    () =>
      copilotSkills.map((skill) => ({
        id: `skill-${skill.slug}`,
        type: 'skill',
        name: skill.name,
        description: skill.description,
        icon: 'skills',
        slug: skill.slug,
      })),
    [copilotSkills],
  );
  const skillSlugs = useMemo(() => new Set(copilotSkills.map((s) => s.slug)), [copilotSkills]);

  // Skills usually resolve after the editor is created, and tiptap can't rebuild
  // its plugins — so the extensions read through refs instead of capturing arrays.
  const skillCommandsRef = useRef(skillCommands);
  skillCommandsRef.current = skillCommands;
  const skillSlugsRef = useRef(skillSlugs);
  skillSlugsRef.current = skillSlugs;
  const getSkillCommands = useCallback(() => skillCommandsRef.current, []);
  const getSkillSlugs = useCallback(() => skillSlugsRef.current, []);

  const builtinCommands = useMemo<BuiltinCommand[]>(
    () => [
      // /new is only available in sidebar/fullscreen (no resetSession prop).
      // The home page passes resetSession because it creates a new session on
      // every submit — /new doesn't make sense there.
      ...(resetSession == null
        ? [
            {
              id: 'new',
              type: 'builtin' as const,
              name: 'new',
              description: 'Start a new chat',
              icon: 'add' as const,
              execute: () => {
                // reset() is added by useCopilotSession hook at runtime
                // oxlint-disable typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
                (
                  sessionRef.current as CopilotSession & {
                    reset: () => void;
                  }
                ).reset();
                // oxlint-enable typescript/consistent-type-assertions
                track('aiAssistant', 'newChatClicked', {});
              },
            },
          ]
        : []),
      {
        id: 'add-files',
        type: 'builtin',
        name: 'add-files',
        description: 'Attach files to your message',
        icon: 'paperclip',
        execute: () => fileUploadAreaRef.current?.triggerFileInput(),
      },
      {
        id: 'history',
        type: 'builtin',
        name: 'history',
        description: 'View all chats',
        icon: 'time-window',
        execute: () => {
          toggle('copilot-history');
        },
      },
      {
        id: 'feedback',
        type: 'builtin',
        name: 'feedback',
        description: 'Give feedback on the Agent',
        icon: 'chat-question',
        execute: () => {
          track('aiAssistant', 'feedbackLinkClicked', {});
          void showFeedbackModal({
            feature: 'AI Agent',
            onSubmit: (values) => {
              track('aiAssistant', 'feedbackFormSubmitted', {
                Feedback: values.feedback,
              });
            },
          });
        },
      },
    ],
    [track, showFeedbackModal, resetSession],
  );

  const suggestionStateRef = useRef<SuggestionRenderState>({ isOpen: false });
  const [suggestionRender] = useState(() => createSuggestionRender(suggestionStateRef.current));

  const editor = useEditor({
    extensions: [
      Document,
      TiptapParagraph,
      TiptapText,
      HardBreak,
      UndoRedo,
      Placeholder.configure({
        placeholder: placeholderText,
      }),
      SlashCommandExtension.configure({
        builtinCommands,
        getSkillCommands,
        suggestion: {
          render: suggestionRender,
        },
      }),
      SkillHighlightExtension.configure({
        getSlugs: getSkillSlugs,
      }),
    ],
    editorProps: {
      attributes: {
        'aria-label': 'Send message to the Agent',
        id: 'copilot-chat-input',
        role: 'textbox',
      },
      handleKeyDown: (_view, event) => {
        // Enter to submit (without Shift), but not during IME composition
        // and not while the slash command menu is open (it handles Enter itself)
        if (event.key === 'Enter' && !event.shiftKey && !event.isComposing && !suggestionStateRef.current.isOpen) {
          event.preventDefault();
          formRef.current?.requestSubmit();
          return true;
        }

        // Arrow up at the top of the input: pull all queued messages back
        if (event.key === 'ArrowUp' && visibleQueuedMessagesRef.current.length > 0) {
          const editor_ = _view.state;
          const isAtStart = editor_.selection.from <= 1 && editor_.selection.empty;
          if (isAtStart) {
            event.preventDefault();
            const cleared = sessionRef.current.clearQueuedMessages();
            const texts = cleared
              .filter((m): m is CopilotMessage & { text: string } => 'text' in m && m.type === 'text')
              .map((m) => m.text);
            const restoredText = texts.join('\n');
            const combined = [restoredText, inputRef.current].filter((t) => t.trim() !== '').join('\n\n');
            setInputRef.current(combined);
            return true;
          }
        }

        return false;
      },
      handlePaste: (_view, event) => {
        // Extract image files from clipboard
        const items = Array.from(event.clipboardData?.items ?? []);
        const imageFiles = items
          .filter((item: DataTransferItem) => item.type.startsWith('image/'))
          .map((item: DataTransferItem) => item.getAsFile())
          .filter((file): file is File => file != null);

        if (imageFiles.length > 0) {
          event.preventDefault();
          handleFilesRef.current(imageFiles);
          return true;
        }

        return false;
      },
    },
    onUpdate: ({ editor: updatedEditor }) => {
      setInput(getEditorText(updatedEditor.state.doc));
    },
  });

  // Keep placeholder in sync when it changes (e.g. files added/removed)
  useEffect(() => {
    syncPlaceholder(editor, placeholderText);
  }, [editor, placeholderText]);

  // Sync external input changes into the editor (queued message restore, URL
  // prefill, stop-button text restoration).
  useEffect(() => {
    if (editor == null || editor.isDestroyed) return;
    const currentText = getEditorText(editor.state.doc);
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
    if (currentText !== input) {
      if (input === '') {
        editor.commands.clearContent(false);
      } else {
        editor.commands.setContent(buildDocFromText(input), { emitUpdate: false });
      }
    }
  }, [editor, input]);

  // Click on the wrapper focuses the editor
  const handleWrapperClick = useCallback(
    (event: React.MouseEvent) => {
      // Don't steal focus from buttons inside the wrapper
      if (
        // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
        (event.target as HTMLElement).closest('button') != null
      )
        return;
      // Don't override cursor/selection when clicking inside the editor itself —
      // ProseMirror already handles focus and selection natively. Without this
      // guard, mouse text selection breaks because the click event (which fires
      // after mouseup) would jump the cursor to the end.
      if (
        editor != null &&
        editor.view.dom.contains(
          // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
          event.target as Node,
        )
      )
        return;
      editor?.commands.focus('end');
    },
    [editor],
  );

  const wrapperClassName = [
    styles.tiptapEditorWrapper,
    fileChips.length > 0 ? styles.tiptapEditorWrapperWithFiles : '',
    customWrapperClassName ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const uploadButton = (
    <Button
      icon="paperclip"
      isIconOnly={true}
      variant="secondary"
      type="button"
      size="sm"
      style={{ border: 'none' }}
      isDisabled={networkStatus === 'offline'}
      onClick={handleUploadButtonClick}
    />
  );

  const settingsMenu = (
    <DropdownMenu>
      <DropdownMenuTriggerButton
        isIconOnly
        icon="properties"
        variant="secondary"
        type="button"
        size="sm"
        style={{ border: 'none' }}
      />
      <DropdownMenuContent>
        <DropdownMenuGroup label="Capabilities">
          <DropdownMenuCheckboxItem
            isChecked={enableWebSearch}
            onCheckedChange={(checked) => {
              session.setWebSearchEnabled(checked);
              track('aiAssistant', 'webSearchToggled', { Enabled: checked });
            }}
            shouldCloseOnClick={false}
          >
            Enable web search
          </DropdownMenuCheckboxItem>
        </DropdownMenuGroup>
        <DropdownMenuGroup label="Settings">
          {workspace != null ? (
            <DropdownMenuItem
              isDisabled={lastMatch?.id === 'workspace_settings_ai_business_profile'}
              onClick={() => {
                track('aiAssistant', 'editBusinessProfileClicked', { 'Click Location': 'menu' });
                safeNavigate(
                  router.buildLocation({
                    to: '/workspaces/$workspaceId/settings/ai-business-profile',
                    params: { workspaceId: workspace.id },
                  }).href,
                );
              }}
            >
              Add context
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            onClick={() => {
              track('aiAssistant', 'feedbackLinkClicked', {});
              void showFeedbackModal({
                feature: 'AI Agent',
                onSubmit: (values) => {
                  track('aiAssistant', 'feedbackFormSubmitted', {
                    Feedback: values.feedback,
                  });
                },
              });
            }}
          >
            Give feedback
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      {uploadError != null ? (
        <Banner
          title="Upload error"
          variant="critical"
          mb="100"
          shouldShowDismissButton={true}
          onClose={() => setUploadError(null)}
          description={
            <Box as="span" whiteSpace="pre-line">
              {uploadError}
            </Box>
          }
        />
      ) : null}
      {error != null ? (
        <Banner
          title="Error"
          variant="critical"
          mb="100"
          action={{
            label: 'Retry',
            onClick: () => {
              if (lastInput.current != null || lastFiles.current.length > 0) {
                submit(lastInput.current ?? '', lastFiles.current);
              }
            },
          }}
          shouldShowDismissButton={true}
          onClose={() => setError(null)}
          description={error}
        />
      ) : null}
      {isLoading && visibleQueuedMessages.length > 0 ? (
        <Box
          mb="100"
          p="100"
          display="flex"
          flexDirection="column"
          gap="075"
          className={chatStyles.queuedMessagesContainer}
        >
          <InlineStack
            alignBlock="center"
            alignInline="space-between"
            pb="050"
            className={chatStyles.queuedMessagesHeader}
          >
            <InlineStack alignBlock="center" gap="050">
              <Icon name="time-window" size="sm" color="secondary" />
              <Text size="xs" color="secondary" weight="medium">
                Queued messages
              </Text>
            </InlineStack>
            <InlineStack alignBlock="center" gap="050">
              <Text size="xs" color="secondary">
                Press
              </Text>
              <KeyboardShortcut hotkey="ArrowUp" />
              <Text size="xs" color="secondary">
                to edit
              </Text>
            </InlineStack>
          </InlineStack>
          <QueuedMessage messages={visibleQueuedMessages} />
        </Box>
      ) : null}
      <FileUploadArea
        ref={fileUploadAreaRef}
        onFilesSelected={handleFiles}
        disabled={networkStatus === 'offline'}
        style={{ position: 'relative' }}
        acceptedMimeTypes={Object.keys(SUPPORTED_FILE_TYPES)}
      >
        <Form
          ref={formRef}
          onSubmit={async (event) => {
            event.preventDefault();

            if (!canSubmit) {
              return;
            }

            if (networkStatus === 'offline') {
              return;
            }

            // Checked here, not on the button, so Enter is covered too.
            if (!allowSendOrStop()) {
              return;
            }

            // Only update retry refs if this is a direct send (not queued).
            // If the session is busy, sendMessage will queue — don't overwrite
            // the retry refs for the in-flight message.
            if (status !== 'sending' && status !== 'receiving' && status !== 'recovering') {
              lastInput.current = input;
              lastFiles.current = Array.from(fileUpload.files);
            }
            await submit(input, Array.from(fileUpload.files));
          }}
          position="relative"
          autoLoading={false}
        >
          {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- wrapper click focuses the editor, keyboard users focus directly */}
          <div className={wrapperClassName} onClick={handleWrapperClick}>
            <EditorContent editor={editor} className={styles.tiptapEditor} />

            {/* File chips positioned absolutely */}
            {fileChips.length > 0 ? (
              <Box
                style={{
                  position: 'absolute',
                  top: themeVars.space['100'],
                  left: themeVars.space['100'],
                  right: themeVars.space['600'],
                  zIndex: 1,
                  pointerEvents: 'none',
                }}
              >
                <Box style={{ pointerEvents: 'auto' }}>
                  <Attachments
                    files={fileChips}
                    variant="inline"
                    readOnly={false}
                    onRemove={fileUpload.removeFile}
                    session={session}
                  />
                </Box>
              </Box>
            ) : null}

            <Box
              display="flex"
              alignItems="flex-end"
              justifyContent="space-between"
              style={{
                position: 'absolute',
                bottom: themeVars.space['100'],
                left: themeVars.space['100'],
                right: themeVars.space['100'],
                pointerEvents: 'none',
              }}
            >
              <Box display="flex" alignItems="center" gap="050" style={{ pointerEvents: 'auto' }}>
                {showApprovalModeControl ? (
                  <DropdownMenu onOpenChange={setIsApprovalDropdownOpen}>
                    <Tooltip
                      content={
                        approvalMode === 'auto'
                          ? 'Your agent changes live data without asking.'
                          : 'You approve each change to live data.'
                      }
                      placement="top"
                      isCustom={true}
                      isDisabled={isApprovalDropdownOpen}
                      // Don't trigger on focus — after the dropdown closes, focus returns
                      // to the trigger and would otherwise spuriously re-show the tooltip.
                      // Hover-only is sufficient since the same copy lives in the menu items.
                      floatingPluginOptions={{ focus: { enabled: false } }}
                    >
                      {(context) => (
                        <DropdownMenuTriggerButton
                          variant="secondary"
                          type="button"
                          size="sm"
                          className={
                            approvalMode === 'auto'
                              ? `${styles.borderlessTrigger} ${styles.approvalModeTriggerAuto}`
                              : styles.borderlessTrigger
                          }
                          ref={context.reference.setReference}
                          {...context.reference.getReferenceProps()}
                        >
                          <Strong>{approvalMode === 'auto' ? 'Auto' : 'Ask'}</Strong>
                        </DropdownMenuTriggerButton>
                      )}
                    </Tooltip>
                    <DropdownMenuContent className={styles.approvalModeMenu}>
                      <DropdownMenuItem
                        trailingIcon={approvalMode === 'ask' ? 'check' : undefined}
                        onClick={handleSelectAskMode}
                      >
                        <Box className={styles.approvalModeItemBody} display="flex" flexDirection="column" gap="025">
                          <Text>Ask</Text>
                          <Paragraph variant="secondary" size="xs">
                            You approve each change to live data.
                          </Paragraph>
                        </Box>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        trailingIcon={approvalMode === 'auto' ? 'check' : undefined}
                        onClick={handleSelectAutoMode}
                      >
                        <Box className={styles.approvalModeItemBody} display="flex" flexDirection="column" gap="025">
                          <Text>Auto</Text>
                          <Paragraph variant="secondary" size="xs">
                            The Agent changes live data without asking.
                          </Paragraph>
                        </Box>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
                {/* Without the Ask/Auto dropdown, upload + settings keep their legacy spot on the left. */}
                {!showApprovalModeControl ? (
                  <>
                    {uploadButton}
                    {settingsMenu}
                  </>
                ) : null}
                <ModelPicker session={session} />
                <TokenUsage session={session} />
                {/* The control row has fixed insets and no truncation; at the minimum sidebar
                    width both pills plus the controls can crowd the send button, so the rarer
                    memory pill wins and the usage signal stays visible via the thread card. */}
                {showMemoryPill ? null : <AgentUsagePill session={session} />}
              </Box>
              <Box display="flex" alignItems="center" gap="075" style={{ pointerEvents: 'auto' }}>
                {showApprovalModeControl ? (
                  <>
                    {uploadButton}
                    {settingsMenu}
                  </>
                ) : null}
                {isLoading ? (
                  <Button
                    icon="stop-filled"
                    isIconOnly={true}
                    variant="secondary"
                    type="button"
                    size="sm"
                    onClick={handleStop}
                    className={styles.chatActionButton}
                  />
                ) : (
                  <Button
                    icon="arrow-up"
                    isIconOnly={true}
                    variant="primary"
                    type="submit"
                    size="sm"
                    isDisabled={networkStatus === 'offline' || !canSubmit}
                    className={styles.chatActionButton}
                  />
                )}
              </Box>
            </Box>
          </div>
        </Form>
      </FileUploadArea>
      <FooterStatment input={input} />
    </>
  );
}
